import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { datePickerClearableProps, datePickerFormItemProps } from "@/utils/componentUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Col, DatePicker, Form, Modal, Row } from "antd";
import { useTranslation } from "react-i18next";
import { useRecalculatePredictedCommissions } from "../../../../common/api/queries";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import { RecalculatePredictedCommissions } from "../../../commissions/predicted/types";
import { AgentSelect } from "../../../enumerations/components/form/AgentSelect";
import { InstitutionTreeSelect } from "../../../enumerations/components/form/InstitutionTreeSelect";
import { ProductTreeSelect } from "../../../enumerations/components/form/ProductTreeSelect";

interface Props {
  open: boolean;
  onFormCancel: () => void;
}

export const RecalculatePredictedCommissionsModalForm = ({ open, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<RecalculatePredictedCommissions>();
  const institutionIds = Form.useWatch("institutionIds", form);

  const recalculatePredictedCommissions = useRecalculatePredictedCommissions();

  const handleFormSubmit = () => {
    form.validateFields().then((values) =>
      recalculatePredictedCommissions.mutate(values, {
        onError: (error) => setErrorsToForm(form, "contract.attrs.predictedCommissions", error.violations),
        onSuccess: onFormCancel,
      }),
    );
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={t("contract.helpers.predictedCommissionsRecalculation")}
      cancelText={t("common.cancel")}
      okText={t("common.recalculate")}
      confirmLoading={recalculatePredictedCommissions.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="recalculatePredictedCommissionsForm">
        <Row gutter={rowGutter}>
          <Col span={12}>
            <Form.Item name="institutionIds" label={t("contract.attrs.predictedCommissions.institutionIds")}>
              <InstitutionTreeSelect />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="productIds"
              label={
                <LabelWithTooltip
                  tooltip={t("components.productTreeSelect.labelHint")}
                  label={t("components.productTreeSelect.label")}
                />
              }
            >
              <ProductTreeSelect institutionIdsFilter={institutionIds} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item name="gainerIds" label={t("contract.attrs.predictedCommissions.gainerIds")}>
              <AgentSelect allowClear isMultiple />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={12}>
            <Form.Item
              name="createdDateMin"
              label={t("contract.attrs.predictedCommissions.createdDateMin")}
              rules={[validations.none]}
              {...datePickerFormItemProps}
            >
              <DatePicker {...datePickerClearableProps} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="createdDateMax"
              label={t("contract.attrs.predictedCommissions.createdDateMax")}
              rules={[validations.none]}
              {...datePickerFormItemProps}
            >
              <DatePicker {...datePickerClearableProps} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
